import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Shield, User, ArrowRight, Lock, Mail, UserPlus, Wifi, WifiOff, AlertTriangle } from 'lucide-react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useFirebase } from '../context/FirebaseContext';
import { auth } from '../firebase';

export default function LoginPage() {
  const { signIn, signUp, signInWithGoogle, resetPassword, user } = useUser();
  const { dbConnected } = useFirebase();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isRegister, setIsRegister] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { loading: authLoading } = useFirebase();

  const redirectPath = searchParams.get('redirect') || '/member-portal';
  const emailParam = searchParams.get('email') || '';
  const modeParam = searchParams.get('mode');

  useEffect(() => {
    if (emailParam) setEmail(emailParam);
    if (modeParam === 'register') setIsRegister(true);
  }, [emailParam, modeParam]);

  useEffect(() => {
    if (!authLoading && user) {
      navigate(redirectPath);
    }
  }, [user, authLoading, navigate, redirectPath]);
  const networkError = error.includes('handshake failed') || error.includes('network');
  const [inIframe, setInIframe] = useState(false);

  useEffect(() => {
    try {
      setInIframe(window.self !== window.top);
    } catch (e) {
      setInIframe(true);
    }
  }, []);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (isRegister && !acceptedTerms) {
      setError('You must accept the liability waiver to register.');
      return;
    }
    
    setLoading(true);

    try {
      if (isForgotPassword) {
        await resetPassword(email);
        setResetSent(true);
        setLoading(false);
        return;
      }

      if (isRegister) {
        await signUp(email, password, displayName);
        try {
          const fbUser = auth.currentUser;
          if (fbUser) {
            await fetch('/api/claim-guest-data', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: fbUser.email, uid: fbUser.uid })
            });
          }
        } catch (e) {}
      } else {
        await signIn(email, password);
        try {
          const fbUser = auth.currentUser;
          if (fbUser) {
            await fetch('/api/claim-guest-data', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: fbUser.email, uid: fbUser.uid })
            });
          }
        } catch (e) {}
      }
      navigate(redirectPath);
    } catch (err: any) {
      const errorCode = err.code || (err.message?.includes('auth/') ? err.message.match(/auth\/[a-z-]+/)?.[0] : null);
      
      if (errorCode === 'auth/network-request-failed' || err.message.includes('network')) {
        setError('Network Protocol Failure: Security handshake failed. This is often blocked by browser privacy shields or strict corporate firewalls. Attempt a page refresh or try disabling tracking protection for this site.');
      } else if (errorCode === 'auth/email-already-in-use') {
        setError('Operational Alert: This email is already registered in the Syndicate database. If this is you, please login instead.');
      } else if (errorCode === 'auth/invalid-credential' || errorCode === 'auth/wrong-password' || errorCode === 'auth/user-not-found') {
        setError('Credential Mismatch: The coordinates provided do not match our records. Please verify your email and password.');
      } else if (errorCode === 'auth/weak-password') {
        setError('Security Protocol Violation: Password is too weak. Please use at least 6 characters.');
      } else {
        setError(err.message || 'Authentication failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ows-sand/20 flex items-center justify-center p-4 pt-32 pb-20">
      <div className="w-full max-w-xl">
        <div className="flex justify-center mb-8">
          <div className={`px-4 py-1.5 rounded-full border flex items-center gap-2 transition-all ${dbConnected ? 'bg-green-50 border-green-100 text-green-600' : 'bg-red-50 border-red-100 text-red-600 animate-pulse'}`}>
            {dbConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
            <span className="text-[9px] font-bold uppercase tracking-[0.2em]">{dbConnected ? 'Syndicate Link Established' : 'Link Interrupted'}</span>
          </div>
        </div>

        <div className="text-center mb-8">
          <h1 className="text-xs font-bold uppercase tracking-[0.5em] text-ows-teal mb-2 font-mono">explorer PORTAL</h1>
          <p className="text-ows-dark/40 font-serif italic text-sm">Secure entry to your Syndicate profile and dispatches.</p>
        </div>

        <div className="bg-white rounded-[3.5rem] shadow-2xl border border-ows-teal/5 overflow-hidden">
          <div className="p-10">
            <div className="space-y-6">
              <AnimatePresence>
                {(inIframe || !dbConnected) && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-5 rounded-3xl mb-6 flex flex-col gap-3 group transition-all ${!dbConnected ? 'bg-red-50 border border-red-100' : 'bg-ows-teal/5 border border-ows-teal/10'}`}
                  >
                    <div className="flex items-center gap-3">
                      <Shield className={`w-4 h-4 ${!dbConnected ? 'text-red-500' : 'text-ows-teal'}`} />
                      <p className={`text-[9px] font-bold uppercase tracking-widest font-mono ${!dbConnected ? 'text-red-600' : 'text-ows-teal'}`}>
                        {!dbConnected ? 'LINK BLOCKED: PRIVACY SHIELDS ACTIVE' : 'OPTIMUM ACCESS: TAB PROTOCOL'}
                      </p>
                    </div>
                    <a 
                      href={window.location.href} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-full py-3.5 bg-white border border-ows-teal/20 text-ows-teal rounded-xl font-bold text-[9px] tracking-[0.3em] uppercase hover:bg-ows-teal hover:text-white transition-all flex items-center justify-center gap-2 font-mono shadow-sm"
                    >
                      Launch Secure Portal <ArrowRight className="w-3 h-3" />
                    </a>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="text-center space-y-2">
                <h2 className="text-2xl font-display font-light uppercase tracking-[0.3em] text-ows-dark">
                  {isForgotPassword ? 'RESET PASSWORD' : isRegister ? 'JOIN THE SYNDICATE' : 'SECURE ACCESS'}
                </h2>
                <p className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/30 font-mono">
                  {isForgotPassword ? 'CREDENTIAL RECOVERY' : isRegister ? 'CREDENTIAL AUTHORIZATION' : 'ENTER DISPATCH COORDINATES'}
                </p>
              </div>

              {resetSent ? (
                <div className="p-6 bg-ows-teal/10 rounded-2xl border border-ows-teal/20 text-center space-y-4">
                  <Mail className="h-8 w-8 text-ows-teal mx-auto" />
                  <h3 className="text-sm font-bold uppercase tracking-widest text-ows-dark">Recovery Link Sent</h3>
                  <p className="text-[10px] text-ows-dark/60 font-mono">A secure password reset link has been dispatched to {email}. Please check your inbox.</p>
                  <button 
                    onClick={() => {
                      setIsForgotPassword(false);
                      setResetSent(false);
                    }}
                    className="mt-4 px-6 py-3 bg-white text-ows-dark hover:bg-ows-sand border border-ows-teal/10 rounded-xl text-[9px] font-bold uppercase tracking-widest transition-all w-full"
                  >
                    Return to Login
                  </button>
                </div>
              ) : (
                <form onSubmit={handleAuth} className="space-y-4 pt-4">
                  {isRegister && !isForgotPassword && (
                    <div className="relative">
                      <User className="absolute left-6 top-1/2 -translate-y-1/2 h-4 w-4 text-ows-dark/20" />
                      <input 
                        type="text"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        placeholder="CALLSIGN"
                        required
                        className="w-full bg-ows-sand/5 border border-ows-teal/5 pl-14 pr-6 py-4 rounded-xl text-ows-dark placeholder:text-ows-dark/20 focus:outline-none focus:border-ows-teal/20 transition-all tracking-[0.3em] text-[10px] font-mono"
                      />
                    </div>
                  )}
                  <div className="relative">
                    <Mail className="absolute left-6 top-1/2 -translate-y-1/2 h-4 w-4 text-ows-dark/20" />
                    <input 
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="EMAIL ADDRESS"
                      required
                      className="w-full bg-ows-sand/5 border border-ows-teal/5 pl-14 pr-6 py-4 rounded-xl text-ows-dark placeholder:text-ows-dark/20 focus:outline-none focus:border-ows-teal/20 transition-all tracking-[0.3em] text-[10px] font-mono"
                    />
                  </div>
                  {!isForgotPassword && (
                    <div className="relative">
                      <Lock className="absolute left-6 top-1/2 -translate-y-1/2 h-4 w-4 text-ows-dark/20" />
                      <input 
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="ACCESS PASSWORD"
                        required
                        className="w-full bg-ows-sand/5 border border-ows-teal/5 pl-14 pr-6 py-4 rounded-xl text-ows-dark placeholder:text-ows-dark/20 focus:outline-none focus:border-ows-teal/20 transition-all tracking-[0.3em] text-[10px] font-mono"
                      />
                    </div>
                  )}
                  
                  {isRegister && !isForgotPassword && (
                    <div className="p-5 bg-ows-sand/10 rounded-2xl border border-ows-dark/5 mt-2">
                      <div className="flex items-start gap-3">
                        <input 
                          type="checkbox" 
                          id="terms"
                          required
                          checked={acceptedTerms}
                          onChange={(e) => setAcceptedTerms(e.target.checked)}
                          className="mt-0.5 w-4 h-4 accent-ows-teal rounded cursor-pointer shrink-0"
                        />
                        <label htmlFor="terms" className="text-[8px] uppercase tracking-widest text-ows-dark/50 leading-relaxed cursor-pointer font-mono">
                          Accept liability waiver & syndicate release protocol.
                        </label>
                      </div>
                    </div>
                  )}

                  {!isRegister && !isForgotPassword && (
                    <div className="flex justify-end">
                      <button 
                        type="button" 
                        onClick={() => setIsForgotPassword(true)}
                        className="text-[9px] font-bold text-ows-dark/40 hover:text-ows-teal uppercase tracking-widest transition-colors font-mono"
                      >
                        Forgot Password?
                      </button>
                    </div>
                  )}

                  {error && (
                    <p className="text-[8px] text-red-500 font-bold uppercase tracking-[0.2em] text-center pt-2 font-mono">{error}</p>
                  )}

                  <button 
                    type="submit"
                    disabled={loading}
                    className="w-full py-5 bg-ows-teal text-white rounded-xl font-bold text-[9px] tracking-[0.4em] uppercase hover:bg-ows-dark transition-all flex items-center justify-center gap-3 shadow-lg shadow-ows-teal/5 font-mono group"
                  >
                    {loading ? 'AUTHORIZING...' : isForgotPassword ? 'SEND RECOVERY LINK' : 'PORTAL ENTRY'} 
                    <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                  </button>
                </form>
              )}

              <div className="relative py-4 flex items-center justify-center">
                <div className="w-full border-t border-ows-teal/5 absolute"></div>
                <span className="bg-white px-4 text-right text-[8px] font-bold uppercase tracking-[0.4em] text-ows-dark/20 relative font-mono">OR ALTERNATE PROTOCOL</span>
              </div>

              <button 
                type="button"
                onClick={async () => {
                  setError('');
                  if (isRegister && !acceptedTerms) {
                    setError('LIABILITY WAIVER REQUIRED');
                    return;
                  }
                  try {
                    await signInWithGoogle();
                    navigate(redirectPath);
                  } catch (err: any) {
                    if (err.code !== 'auth/popup-closed-by-user') {
                      setError('GOOGLE PROTOCOL INTERRUPTED');
                    }
                  }
                }}
                disabled={loading}
                className="w-full py-5 bg-white border border-ows-teal/10 text-ows-dark rounded-xl font-bold text-[9px] tracking-[0.4em] uppercase hover:bg-ows-sand/10 transition-all flex items-center justify-center gap-3 font-mono"
              >
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                GOOGLE ACCESS
              </button>

              <div className="text-center pt-2">
                <button 
                  type="button"
                  onClick={() => {
                    if (isForgotPassword) {
                      setIsForgotPassword(false);
                    } else {
                      setIsRegister(!isRegister);
                    }
                  }}
                  className="text-[9px] font-bold uppercase tracking-[0.2em] text-ows-teal/60 hover:text-ows-dark transition-colors font-mono"
                >
                  {isForgotPassword ? 'RETURN TO LOGIN' : isRegister ? 'EXISTING CALLSIGN? LOGIN' : 'NEW EXPLORER? REGISTER ACCOUNT'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <Link to="/admin-login" className="text-[8px] font-bold uppercase tracking-[0.5em] text-ows-dark/20 hover:text-ows-teal transition-colors">
            Administrative Overwatch Access
          </Link>
        </div>
      </div>
    </div>
  );
}
